import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ColumnDef } from "@tanstack/react-table";
import {
  ChevronRight,
  Flame,
  Hash,
  Network,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { confirmAsync, pushToast } from "@/components/Toaster";
import {
  type ChannelListEntry,
  type ChannelListType,
  type ChannelMatchType,
  type ChannelSummary,
  type TelemetrImportRequest,
  useChannelLists,
  useChannels,
  useCreateChannel,
  useCreateListEntry,
  useDeleteChannel,
  useDeleteListEntry,
  useUpdateListEntry,
  useExpandSimilar,
  useTelemetrCredentials,
  useTelemetrImport,
  useUpdateChannel,
} from "@/lib/channels";
import {
  useAddWarmupChannelsBulk,
  useDeleteWarmupChannel,
  useWarmupChannels,
} from "@/lib/warmup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { Badge, StatusBadge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipProvider } from "@/components/ui/tooltip";
import { SimpleSelect } from "@/components/ui/select";
import { ActionMenu } from "@/components/ui/dropdown-menu";
import { DataTable } from "@/components/ui/table";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HelpSection } from "@/components/HelpSection";
import { ChannelDetailDrawer } from "@/components/channels/ChannelDetailDrawer";
import { BulkChannelDialog } from "@/components/channels/BulkChannelDialog";

type Tab = "list" | "blacklist" | "telemetr" | "warmup";

/**
 * What to show in place of a handle. Invite-link channels have no username and
 * often no title until the first successful read, so they'd otherwise render as
 * "unknown" and be indistinguishable from each other in the list.
 */
function channelDisplayName(
  c: ChannelSummary,
  t: (key: string) => string,
): string {
  if (c.username) return `@${c.username}`;
  // A known title beats a raw id: linked discussion groups never have a
  // username, so the list used to show them as bare numbers with the actual
  // name demoted to the subtitle.
  if (c.title) return c.invite_hash ? `🔒 ${c.title}` : c.title;
  if (c.invite_hash) return `🔒 ${c.invite_hash.slice(0, 10)}…`;
  if (c.tg_channel_id != null) return String(c.tg_channel_id);
  return t("channels.unknown_channel");
}

const PARSING_STATUSES: ChannelSummary["parsing_status"][] = [
  "queued",
  "parsing",
  "pending_join",
  "deferred",
  "done",
  "skipped",
  "skipped_no_users",
  "failed",
  "failed_private",
];

export function ChannelsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("list");
  const channels = useChannels();

  const subtitle = useMemo(() => {
    const data = channels.data ?? [];
    if (data.length === 0) return t("channels.subtitle");
    const queued = data.filter((c) => c.parsing_status === "queued").length;
    const done = data.filter((c) => c.parsing_status === "done").length;
    return t("channels.subtitle_counts", { total: data.length, queued, done });
  }, [channels.data, t]);

  return (
    <TooltipProvider delayDuration={300}>
      <section className="mx-auto max-w-7xl space-y-6 px-6 py-8">
        <PageHeader
          title={t("channels.title")}
          subtitle={subtitle}
          tabs={
            <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
              <TabsList>
                <TabsTrigger value="list">{t("channels.tab.list")}</TabsTrigger>
                <TabsTrigger value="blacklist">
                  {t("channels.tab.blacklist")}
                </TabsTrigger>
                <TabsTrigger value="telemetr">
                  {t("channels.tab.telemetr")}
                </TabsTrigger>
                <TabsTrigger value="warmup">
                  {t("channels.tab.warmup_short")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          }
        />

        {tab === "list" && <ListTab />}
        {tab === "blacklist" && <ListsTab />}
        {tab === "telemetr" && <TelemetrTab onGoToList={() => setTab("list")} />}
        {tab === "warmup" && <WarmupChannelsTab />}

        <HelpSection
          i18nKey="channels.help"
          paragraphs={4}
          list={{
            titleKey: "channels.help.list_title",
            itemsKey: "channels.help.list_items",
            itemCount: 4,
          }}
        />
      </section>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Tab: list of tracked channels
// ---------------------------------------------------------------------------

function ListTab() {
  const { t } = useTranslation();
  const channels = useChannels();
  const create = useCreateChannel();
  const update = useUpdateChannel();
  const del = useDeleteChannel();
  const expandSimilar = useExpandSimilar();
  // Depth 1 expands the seeds; depth 2 also expands the freshly-found channels
  // (~2× the discovery but more recommendation calls) — off by default, opt-in.
  const [deepExpand, setDeepExpand] = useState(false);

  const onExpandSimilar = async () => {
    try {
      const res = await expandSimilar.mutateAsync({ depth: deepExpand ? 2 : 1 });
      pushToast({
        kind: res.added > 0 ? "success" : "info",
        text: t("channels.expand_similar.done", { added: res.added, found: res.found }),
      });
    } catch (err) {
      pushToast({
        kind: "error",
        text: t("channels.expand_similar.error"),
        detail: err instanceof ApiError ? err.detail : undefined,
      });
    }
  };

  const [showBulk, setShowBulk] = useState(false);
  const [quickInput, setQuickInput] = useState("");
  const [quickError, setQuickError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const data = useMemo(() => channels.data ?? [], [channels.data]);
  const detailChannel = useMemo(
    () => data.find((c) => c.id === detailId) ?? null,
    [data, detailId],
  );

  const openDetail = (c: ChannelSummary) => {
    setDetailId(c.id);
    setDrawerOpen(true);
  };

  const onQuickAdd = async (e: FormEvent) => {
    e.preventDefault();
    setQuickError(null);
    const raw = quickInput.trim();
    if (!raw) return;
    // A bare numeric id can never be opened (Telegram needs @username or a
    // link) — say so here instead of adding a row that fails every run.
    if (/^-?\d+$/.test(raw)) {
      setQuickError(t("channels.quick_add.numeric_unsupported"));
      return;
    }
    // Everything else goes to the server as typed: it knows @name, t.me/name,
    // a post link (t.me/name/123) and invite links. Cutting the string up here
    // turned a post link into "123" and broke case-sensitive invite hashes.
    const payload: { username?: string } = { username: raw };
    try {
      await create.mutateAsync(payload);
      setQuickInput("");
    } catch (err) {
      setQuickError(err instanceof ApiError ? err.detail : t("channels.create_error"));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((c) => {
      if (statusFilter !== "all" && c.parsing_status !== statusFilter) return false;
      if (!q) return true;
      return (
        (c.username?.toLowerCase().includes(q) ?? false) ||
        (c.title?.toLowerCase().includes(q) ?? false) ||
        (c.tg_channel_id != null && String(c.tg_channel_id).includes(q))
      );
    });
  }, [data, search, statusFilter]);

  const columns = useMemo<ColumnDef<ChannelSummary, unknown>[]>(
    () => [
      {
        id: "channel",
        header: t("channels.col.channel"),
        accessorFn: (c) =>
          c.username ?? c.title ?? c.invite_hash ?? String(c.tg_channel_id ?? ""),
        cell: ({ row }) => {
          const c = row.original;
          const display = channelDisplayName(c, t);
          return (
            <div className="min-w-0">
              <span className="block truncate font-semibold text-fg-primary">
                {display}
              </span>
              {c.title && c.title !== display && (
                <span className="block truncate text-xs text-fg-muted">
                  {c.title}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "subscribers",
        header: t("channels.col.subscribers"),
        accessorFn: (c) => c.subscribers ?? -1,
        cell: ({ row }) => {
          const s = row.original.subscribers;
          return s != null ? (
            <span className="font-mono text-sm">{formatNumber(s)}</span>
          ) : (
            <span className="text-fg-muted">—</span>
          );
        },
      },
      {
        id: "source",
        header: t("channels.col.source"),
        accessorFn: (c) => c.source,
        cell: ({ row }) => (
          <Badge tone="info" variant="subtle">
            {t(`channels.source.${row.original.source}`)}
          </Badge>
        ),
      },
      {
        id: "status",
        header: t("channels.col.status"),
        accessorFn: (c) => c.parsing_status,
        cell: ({ row }) => {
          const c = row.original;
          const badge = (
            <StatusBadge
              domain="channel"
              value={c.parsing_status}
              label={t(`channels.status.${c.parsing_status}`)}
              dot
            />
          );
          // The tooltip used to repeat the badge's own label. What the
          // operator actually needs is WHY: "не найден" and "кончился дневной
          // лимит резолвов" both render as a red "Ошибка" otherwise.
          const reason = c.last_error
            ? t(`channels.job_error.${c.last_error}`, { defaultValue: c.last_error })
            : t(`channels.status.${c.parsing_status}`);
          return <Tooltip content={reason}>{badge}</Tooltip>;
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">{t("channels.col.actions")}</span>,
        enableSorting: false,
        cell: ({ row }) => {
          const c = row.original;
          const display = channelDisplayName(c, t);
          return (
            <div
              className="flex justify-end"
              onClick={(e) => e.stopPropagation()}
            >
              <ActionMenu
                label={t("channels.col.actions")}
                items={[
                  {
                    label: t("channels.requeue"),
                    icon: <RefreshCw size={15} strokeWidth={2} aria-hidden="true" />,
                    onSelect: () =>
                      update.mutate({ id: c.id, parsing_status: "queued" }),
                    disabled: c.parsing_status === "queued" || update.isPending,
                  },
                  {
                    label: t("channels.delete"),
                    icon: <Trash2 size={15} strokeWidth={2} aria-hidden="true" />,
                    onSelect: async () => {
                      const yes = await confirmAsync({
                        title: t("channels.delete_confirm", { name: display }),
                        confirmKind: "danger",
                        confirmLabel: t("channels.delete"),
                      });
                      if (yes) void del.mutate(c.id);
                    },
                    destructive: true,
                    separatorBefore: true,
                  },
                ]}
              />
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, del, update.isPending],
  );

  return (
    <div className="space-y-4">
      {/* Quick-add: the single, primary add path (no "Add single" modal). */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={onQuickAdd} className="space-y-2">
            <Label htmlFor="quick_channel">{t("channels.quick_add.label")}</Label>
            {/* The buttons don't shrink, so without a floor on the input and
                wrapping on the button row the field collapsed to icon width
                on a normal laptop — the placeholder wasn't even readable. */}
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Input
                id="quick_channel"
                placeholder="@somechannel  /  t.me/+AbCdEf123  /  -1001234567890"
                value={quickInput}
                onChange={(e) => setQuickInput(e.target.value)}
                className="flex-1 sm:min-w-[18rem]"
              />
              <div className="flex max-w-full flex-wrap gap-2 sm:shrink-0">
                <Button
                  type="submit"
                  disabled={!quickInput.trim()}
                  loading={create.isPending}
                  iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
                >
                  {t("channels.quick_add.submit")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBulk(true)}
                  iconLeft={<Upload size={16} strokeWidth={2} aria-hidden="true" />}
                >
                  {t("channels.add_bulk")}
                </Button>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onExpandSimilar}
                    loading={expandSimilar.isPending}
                    disabled={!channels.data || channels.data.length === 0}
                    iconLeft={<Network size={16} strokeWidth={2} aria-hidden="true" />}
                  >
                    {t("channels.expand_similar.button")}
                  </Button>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="deep-expand"
                      checked={deepExpand}
                      onCheckedChange={setDeepExpand}
                    />
                    <Label htmlFor="deep-expand" className="text-xs text-fg-muted cursor-pointer">
                      {t("channels.expand_similar.deep")}
                    </Label>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-fg-muted">{t("channels.quick_add.hint")}</p>
            {quickError && <p className="text-sm text-danger">{quickError}</p>}
          </form>
        </CardContent>
      </Card>

      {channels.isLoading && <p className="text-fg-muted">{t("channels.loading")}</p>}

      {channels.data && (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(c) => c.id}
          onRowClick={openDetail}
          toolbar={
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                placeholder={t("channels.toolbar.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="sm:max-w-xs"
              />
              <SimpleSelect
                className="sm:w-56"
                value={statusFilter}
                onValueChange={setStatusFilter}
                options={[
                  { value: "all", label: t("channels.toolbar.status_all") },
                  ...PARSING_STATUSES.map((s) => ({
                    value: s,
                    label: t(`channels.status.${s}`),
                  })),
                ]}
              />
            </div>
          }
          empty={
            data.length === 0 ? (
              <EmptyState
                icon={Hash}
                title={t("channels.empty_title")}
                description={t("channels.empty_hint")}
                action={
                  <Button
                    onClick={() => setShowBulk(true)}
                    iconLeft={<Upload size={16} strokeWidth={2} aria-hidden="true" />}
                  >
                    {t("channels.add_bulk")}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Hash}
                title={t("channels.toolbar.empty_filtered")}
              />
            )
          }
        />
      )}

      <BulkChannelDialog open={showBulk} onOpenChange={setShowBulk} />
      <ChannelDetailDrawer
        channel={detailChannel}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: blacklist / whitelist — two panels, each with its own "+"
// ---------------------------------------------------------------------------

function ListsTab() {
  const lists = useChannelLists();
  const del = useDeleteListEntry();

  const blacklist = (lists.data ?? []).filter((e) => e.list_type === "blacklist");
  const whitelist = (lists.data ?? []).filter((e) => e.list_type === "whitelist");

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <ListPanel
        listType="blacklist"
        entries={blacklist}
        onDelete={(id) => del.mutate(id)}
      />
      <ListPanel
        listType="whitelist"
        entries={whitelist}
        onDelete={(id) => del.mutate(id)}
      />
    </div>
  );
}

function ListPanel({
  listType,
  entries,
  onDelete,
}: {
  listType: ChannelListType;
  entries: ChannelListEntry[];
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateListEntry();
  const [matchType, setMatchType] =
    useState<ChannelMatchType>("exact_username");
  const [matchValue, setMatchValue] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        list_type: listType,
        match_type: matchType,
        match_value: matchValue.trim(),
        reason: reason.trim() || undefined,
      });
      setMatchValue("");
      setReason("");
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("channels.lists.error"));
    }
  };

  const title =
    listType === "blacklist"
      ? t("channels.lists.blacklist_title")
      : t("channels.lists.whitelist_title");
  const emptyText =
    listType === "blacklist"
      ? t("channels.lists.empty_blacklist")
      : t("channels.lists.empty_whitelist");
  const addLabel =
    listType === "blacklist"
      ? t("channels.lists_redesign.blacklist_add")
      : t("channels.lists_redesign.whitelist_add");

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-fg-primary">{title}</h3>
          <Badge tone="neutral">{entries.length}</Badge>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-2 rounded-lg border border-border bg-bg-primary/40 p-3"
        >
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">
                {t("channels.lists.field.match_type")}
              </Label>
              <SimpleSelect
                value={matchType}
                onValueChange={(v) => setMatchType(v as ChannelMatchType)}
                options={[
                  {
                    value: "exact_username",
                    label: t("channels.lists.match.exact_username"),
                  },
                  {
                    value: "tg_channel_id",
                    label: t("channels.lists.match.tg_channel_id"),
                  },
                  {
                    value: "title_regex",
                    label: t("channels.lists.match.title_regex"),
                  },
                ]}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {t("channels.lists.field.match_value")}
              </Label>
              <Input
                required
                value={matchValue}
                onChange={(e) => setMatchValue(e.target.value)}
                placeholder={t(
                  `channels.lists_redesign.value_placeholder_${matchType}`,
                )}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("channels.lists.field.reason")}</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("channels.lists_redesign.reason_placeholder")}
            />
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={!matchValue.trim()}
              loading={create.isPending}
              iconLeft={<Plus size={15} strokeWidth={2} aria-hidden="true" />}
            >
              {addLabel}
            </Button>
          </div>
        </form>

        {entries.length === 0 ? (
          <p className="text-xs text-fg-muted">{emptyText}</p>
        ) : (
          <ul className="space-y-1">
            {entries.map((e) => (
              <ListEntryRow key={e.id} entry={e} onDelete={() => onDelete(e.id)} />
            ))}
          </ul>
        )}

        <RegexTester entries={entries} />
      </CardContent>
    </Card>
  );
}

function ListEntryRow({
  entry,
  onDelete,
}: {
  entry: ChannelListEntry;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateListEntry();
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState(entry.reason ?? "");

  const saveReason = async () => {
    await update.mutateAsync({ id: entry.id, reason: reason.trim() });
    setEditing(false);
  };

  return (
    <li className="rounded-md border border-border px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        <Badge tone="neutral" variant="outline" className="font-mono uppercase">
          {t(`channels.lists.match.${entry.match_type}`)}
        </Badge>
        <span className="truncate font-mono">{entry.match_value}</span>
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing((v) => !v)}
          >
            {t("channels.lists_redesign.edit_reason")}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete}>
            {t("common.delete")}
          </Button>
        </div>
      </div>
      {editing ? (
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("channels.lists_redesign.reason_placeholder")}
            className="h-8"
          />
          <Button
            size="sm"
            loading={update.isPending}
            onClick={saveReason}
          >
            {t("channels.lists_redesign.reason_save")}
          </Button>
        </div>
      ) : (
        entry.reason && (
          <p className="mt-1 text-xs italic text-fg-muted">— {entry.reason}</p>
        )
      )}
    </li>
  );
}

function RegexTester({ entries }: { entries: ChannelListEntry[] }) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const regexRules = entries.filter((e) => e.match_type === "title_regex");

  const matched = useMemo(() => {
    if (!value.trim()) return null;
    for (const rule of regexRules) {
      try {
        if (new RegExp(rule.match_value, "i").test(value)) return rule.match_value;
      } catch {
        // Invalid stored regex — skip it.
      }
    }
    return "";
  }, [value, regexRules]);

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <p className="text-sm font-medium text-fg-primary">
        {t("channels.lists_redesign.regex_tester_title")}
      </p>
      {regexRules.length === 0 ? (
        <p className="text-xs text-fg-muted">
          {t("channels.lists_redesign.regex_no_rules")}
        </p>
      ) : (
        <>
          <p className="text-xs text-fg-muted">
            {t("channels.lists_redesign.regex_tester_hint")}
          </p>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t("channels.lists_redesign.regex_tester_input")}
          />
          {matched !== null && value.trim() && (
            <Alert tone={matched ? "info" : "success"} icon>
              {matched
                ? t("channels.lists_redesign.regex_match", { value: matched })
                : t("channels.lists_redesign.regex_no_match")}
            </Alert>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: telemetr.me catalog search (key is configured in /settings)
// ---------------------------------------------------------------------------

function TelemetrTab({ onGoToList }: { onGoToList: () => void }) {
  const { t } = useTranslation();
  const creds = useTelemetrCredentials();
  const c = creds.data;
  const ready = c?.has_key && c.last_test_ok;

  if (creds.isLoading) {
    return <p className="text-fg-muted">{t("channels.loading")}</p>;
  }

  if (!ready) {
    return (
      <Alert
        tone="warning"
        title={t("channels.telemetr.no_key_cta_title")}
        action={
          <a
            href="/settings#telemetr"
            className="inline-flex h-8 items-center rounded-lg border border-border px-3 text-sm font-medium text-fg-primary transition-colors hover:bg-bg-elevated/50"
          >
            {t("channels.telemetr.no_key_cta_link")}
          </a>
        }
      >
        {t("channels.telemetr.no_key_cta_body")}
      </Alert>
    );
  }

  return <TelemetrSearchPanel onGoToList={onGoToList} />;
}

function TelemetrSearchPanel({ onGoToList }: { onGoToList: () => void }) {
  const { t } = useTranslation();
  const importMut = useTelemetrImport();
  const [filters, setFilters] = useState<TelemetrImportRequest>({
    limit: 50,
    skip: 0,
    sort_by: "members_count",
    sort_direction: "desc",
    exclude_private: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] =
    useState<Awaited<ReturnType<typeof importMut.mutateAsync>> | null>(null);

  const onRun = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    try {
      const r = await importMut.mutateAsync(filters);
      setResult(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("channels.telemetr.search.error"));
    }
  };

  const update = <K extends keyof TelemetrImportRequest>(
    key: K,
    value: TelemetrImportRequest[K] | undefined,
  ) => setFilters((f) => ({ ...f, [key]: value }));

  const sortOptions = [
    "members_count",
    "members_growth7d",
    "members_growth30d",
    "post_views",
    "er",
    "mentions7_d",
  ].map((v) => ({ value: v, label: t(`channels.telemetr_redesign.sort.${v}`) }));

  return (
    <form onSubmit={onRun} className="space-y-4">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div>
            <p className="text-sm font-semibold text-fg-primary">
              {t("channels.telemetr_redesign.phase_filters")}
            </p>
            <p className="text-xs text-fg-muted">
              {t("channels.telemetr.search.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-xs">{t("channels.telemetr.search.term")}</Label>
              <Input
                value={filters.term ?? ""}
                onChange={(e) => update("term", e.target.value || undefined)}
                placeholder="news, crypto, fitness…"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {t("channels.telemetr.search.language")}
              </Label>
              <Input
                value={filters.language ?? ""}
                onChange={(e) => update("language", e.target.value || undefined)}
                placeholder="ru / en / uk"
              />
            </div>
            {/* No "country" or "channel/group" filter: the Telemetr search the
                backend calls does not take them, so the fields filtered
                nothing while the search (a paid one) ran as if they had. */}
            <div className="space-y-1">
              <Label className="text-xs">
                {t("channels.telemetr.search.min_subs")}
              </Label>
              <Input
                inputMode="numeric"
                value={filters.min_subscribers ?? ""}
                onChange={(e) =>
                  update(
                    "min_subscribers",
                    e.target.value
                      ? Number(e.target.value.replace(/\D/g, ""))
                      : undefined,
                  )
                }
                placeholder="1000"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {t("channels.telemetr.search.max_subs")}
              </Label>
              <Input
                inputMode="numeric"
                value={filters.max_subscribers ?? ""}
                onChange={(e) =>
                  update(
                    "max_subscribers",
                    e.target.value
                      ? Number(e.target.value.replace(/\D/g, ""))
                      : undefined,
                  )
                }
                placeholder="100000"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {t("channels.telemetr.search.sort_by")}
              </Label>
              <SimpleSelect
                value={filters.sort_by ?? "members_count"}
                onValueChange={(v) => update("sort_by", v)}
                options={sortOptions}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {t("channels.telemetr_redesign.import_limit")}
              </Label>
              <Input
                inputMode="numeric"
                value={filters.limit ?? 50}
                onChange={(e) => {
                  const v = Math.max(
                    1,
                    Math.min(100, Number(e.target.value.replace(/\D/g, "") || 50)),
                  );
                  update("limit", v);
                }}
              />
              <p className="text-xs text-fg-muted">
                {t("channels.telemetr_redesign.import_limit_hint")}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-border pt-3 sm:flex-row sm:gap-6">
            <Switch
              checked={!!filters.verified_only}
              onCheckedChange={(v) => update("verified_only", v || undefined)}
              label={t("channels.telemetr.search.verified_only")}
            />
            <Switch
              checked={filters.exclude_private !== false}
              onCheckedChange={(v) =>
                update("exclude_private", v ? undefined : false)
              }
              label={t("channels.telemetr.search.exclude_private")}
            />
          </div>

          <Alert tone="info" icon>
            {t("channels.telemetr_redesign.note_no_preview")}
          </Alert>

          {error && <Alert tone="danger">{error}</Alert>}

          {result && (
            <Alert tone="success" title={t("channels.telemetr.search.result_title")}>
              <p>
                {t("channels.telemetr.search.result_summary", {
                  fetched: result.fetched,
                  total: result.total_in_catalog,
                  inserted: result.inserted,
                  skipped: result.skipped_duplicates,
                })}
              </p>
              <p className="mt-1 text-fg-muted">
                {t("channels.telemetr_redesign.already_imported", {
                  count: result.skipped_duplicates,
                })}
              </p>
              <button
                type="button"
                onClick={onGoToList}
                className="mt-2 inline-flex items-center gap-1 text-sm text-accent hover:underline"
              >
                {t("channels.telemetr_redesign.go_to_list")}
              </button>
            </Alert>
          )}

          <div className="flex justify-end">
            <Button type="submit" loading={importMut.isPending}>
              {t("channels.telemetr_redesign.run")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Tab: per-company warmup channel pool
//
// Kept as a tab (router migration deferred). Collapsed by default to a Card
// with the count + active-pool badge; "Manage" reveals the add form + lists.
// ---------------------------------------------------------------------------

function WarmupChannelsTab() {
  const { t } = useTranslation();
  const channels = useWarmupChannels(true);
  const addBulk = useAddWarmupChannelsBulk();
  const deleteOne = useDeleteWarmupChannel();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [managing, setManaging] = useState(false);

  const companyOwn = (channels.data ?? []).filter((c) => !c.is_global);
  const globals = (channels.data ?? []).filter((c) => c.is_global);
  // The planner warms on the company's own channels ALONE only from this many
  // up (backend _MIN_DIVERSE_CHANNEL_POOL); below it the global ones are mixed
  // in. Calling the pool "the company's" from the first channel was not true.
  const OWN_POOL_MIN = 25;
  const companyActive = companyOwn.length >= OWN_POOL_MIN;
  const poolMixed = companyOwn.length > 0 && !companyActive;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const lines = text
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (lines.length === 0) return;
    try {
      await addBulk.mutateAsync({ channels: lines.map((value) => ({ value })) });
      setText("");
    } catch (e_) {
      setError(e_ instanceof ApiError ? e_.message : String(e_));
    }
  };

  const onDelete = async (id: string, value: string) => {
    const ok = await confirmAsync({
      title: t("channels.warmup.delete_confirm_title"),
      text: t("channels.warmup.delete_confirm_text", { value }),
      confirmKind: "danger",
    });
    if (!ok) return;
    await deleteOne.mutateAsync(id);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent">
                <Flame size={18} strokeWidth={2} aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold text-fg-primary">
                  {t("channels.warmup_redesign.card_title")}
                </p>
                <p className="text-xs text-fg-muted">
                  {t("channels.warmup_redesign.summary", {
                    own: companyOwn.length,
                    globals: globals.length,
                  })}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={companyActive ? "accent" : "warning"} dot>
                {companyActive
                  ? t("channels.warmup_redesign.active_company")
                  : poolMixed
                    ? t("channels.warmup_redesign.active_mixed")
                    : t("channels.warmup_redesign.active_global")}
              </Badge>
              <Button
                size="sm"
                variant={managing ? "ghost" : "outline"}
                onClick={() => setManaging((v) => !v)}
                iconRight={
                  !managing ? (
                    <ChevronRight size={15} strokeWidth={2} aria-hidden="true" />
                  ) : undefined
                }
              >
                {managing
                  ? t("channels.warmup_redesign.back")
                  : t("channels.warmup_redesign.manage")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {managing && (
        <>
          <Alert tone="info" icon title={t("channels.warmup.intro")}>
            {t("channels.warmup.fallback_explainer")}
          </Alert>

          <Card>
            <CardContent className="pt-6">
              <form onSubmit={onSubmit} className="space-y-2">
                <Label htmlFor="warmup_add">{t("channels.warmup.add_label")}</Label>
                <textarea
                  id="warmup_add"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={5}
                  placeholder={t("channels.warmup.add_placeholder") ?? ""}
                  className="flex w-full rounded-lg border border-border bg-bg-secondary px-3 py-2 font-mono text-sm text-fg-primary placeholder:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                />
                <p className="text-xs text-fg-muted">{t("channels.warmup.add_hint")}</p>
                {error && <Alert tone="danger">{error}</Alert>}
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={!text.trim()}
                    loading={addBulk.isPending}
                    iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
                  >
                    {t("channels.warmup.add_submit")}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {channels.isLoading && (
            <p className="text-fg-muted">{t("channels.loading")}</p>
          )}

          {channels.data && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Card>
                <CardContent className="space-y-2 pt-6">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-fg-primary">
                      {t("channels.warmup.company_own_title", {
                        count: companyOwn.length,
                      })}
                    </h3>
                  </div>
                  {companyOwn.length === 0 ? (
                    <p className="text-xs text-fg-muted">
                      {t("channels.warmup.company_own_empty")}
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {companyOwn.map((c) => (
                        <li
                          key={c.id}
                          className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
                        >
                          <span className="font-mono">{c.value}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void onDelete(c.id, c.value)}
                          >
                            {t("channels.warmup.delete")}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="space-y-2 pt-6">
                  <h3 className="text-sm font-semibold text-fg-primary">
                    {t("channels.warmup.globals_title", { count: globals.length })}
                  </h3>
                  <p className="text-xs text-fg-muted">
                    {companyActive
                      ? t("channels.warmup.globals_inactive_hint")
                      : poolMixed
                        ? t("channels.warmup.globals_mixed_hint", {
                            own: companyOwn.length,
                            need: OWN_POOL_MIN,
                          })
                        : t("channels.warmup.globals_active_hint")}
                  </p>
                  {globals.length > 0 && (
                    <ul className="space-y-1">
                      {globals.map((c) => (
                        <li
                          key={c.id}
                          className="flex items-center justify-between rounded-md border border-border bg-bg-primary/40 px-3 py-1.5 text-xs"
                        >
                          <span className="font-mono text-fg-muted">{c.value}</span>
                          <Badge tone="neutral" variant="outline">
                            {t("channels.warmup.global_badge")}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
